import { patchCompanySettings, CompanyQueryKey } from "api/company.api";
import { GlobeEuropeAfricaIcon } from "@heroicons/react/24/outline";
import { Card, CardBody, CardHeader } from "../ui";
import { useNotification } from "hooks/useNotification";
import { FC, useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Modal from "components/Modal";
import {
  NaturvardsverketKeyModal,
  NaturvardsverketKeyFormData,
} from "../NaturvardsverketKeyModal";
import {
  NaturvardsverketContactModal,
  NaturvardsverketContactFormData,
} from "../NaturvardsverketContactModal";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { addYears, isBefore } from "date-fns";
import { Company } from "types/company.type";
import { NaturvardsverketReportingSection } from "./NaturvardsverketReportingSection";
import { NaturvardsverketApiKeysSection } from "./NaturvardsverketApiKeysSection";
import { NaturvardsverketAutomaticSection } from "./NaturvardsverketAutomaticSection";
import { NaturvardsverketContactSection } from "./NaturvardsverketContactSection";
import { NaturvardsverketNotificationsSection } from "../NaturvardsverketNotificationsSection";

interface Props {
  company: Company;
}

const naturvardsverketKeySchema = z.object({
  nvKey: z.string().min(1, "API Key måste anges"),
  nvSecret: z.string().min(1, "API Secret måste anges"),
});

const naturvardsverketContactSchema = z.object({
  nvContactFullName: z.string().min(1, "Kontaktperson måste anges"),
  nvContactEmail: z
    .string()
    .email("Ogiltig e-postadress")
    .min(1, "E-postadress måste anges"),
  nvContactPhone: z.string().min(1, "Telefonnummer måste anges"),
});

export const NaturvardsverketTab: FC<Props> = ({ company }) => {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const [isKeyModalOpen, setIsKeyModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  useEffect(() => {
    document.title = "Naturvårdsverket";
  }, []);

  const {
    register: registerKey,
    handleSubmit: handleSubmitKey,
    formState: { errors: errorsKey },
    reset: resetKey,
  } = useForm<NaturvardsverketKeyFormData>({
    resolver: zodResolver(naturvardsverketKeySchema),
  });

  const {
    register: registerContact,
    handleSubmit: handleSubmitContact,
    formState: { errors: errorsContact },
    reset: resetContact,
  } = useForm<NaturvardsverketContactFormData>({
    resolver: zodResolver(naturvardsverketContactSchema),
    defaultValues: {
      nvContactFullName: company.settings?.nvContactFullName || "",
      nvContactEmail: company.settings?.nvContactEmail || "",
      nvContactPhone: company.settings?.nvContactPhone || "",
    },
  });

  const patchCompanySettingsMutation = useMutation({
    ...patchCompanySettings(),
    onMutate: async ({ payload }) => {
      await queryClient.cancelQueries({
        queryKey: [CompanyQueryKey.GetOne, company.id],
      });

      const previousCompany = queryClient.getQueryData([
        CompanyQueryKey.GetOne,
        company.id,
      ]);

      queryClient.setQueryData(
        [CompanyQueryKey.GetOne, company.id],
        (old: Company | undefined) => {
          if (!old) return old;
          return {
            ...old,
            settings: {
              ...old.settings,
              ...payload,
            },
          };
        },
      );

      return { previousCompany };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CompanyQueryKey.GetOne, company.id],
      });
      notification.success("Inställningar sparade");
    },
    onError: (_error, _variables, context) => {
      if (context?.previousCompany) {
        queryClient.setQueryData(
          [CompanyQueryKey.GetOne, company.id],
          context.previousCompany,
        );
      }
      notification.error("Kunde inte spara inställningar");
    },
  });

  const handleToggle = (isEnabled: boolean) => {
    patchCompanySettingsMutation.mutate({
      id: company.id,
      payload: { isNvActive: isEnabled },
    });
  };

  const onSubmitKey = handleSubmitKey((data: NaturvardsverketKeyFormData) => {
    patchCompanySettingsMutation.mutate({
      id: company.id,
      payload: { nvKey: data.nvKey, nvSecret: data.nvSecret },
    });
    setIsKeyModalOpen(false);
    resetKey();
  });

  const onSubmitContact = handleSubmitContact(
    (data: NaturvardsverketContactFormData) => {
      patchCompanySettingsMutation.mutate({
        id: company.id,
        payload: {
          nvContactFullName: data.nvContactFullName,
          nvContactEmail: data.nvContactEmail,
          nvContactPhone: data.nvContactPhone,
        },
      });
      setIsContactModalOpen(false);
    },
  );

  return (
    <Card>
      <CardHeader
        icon={GlobeEuropeAfricaIcon}
        title="Naturvårdsverket"
        description="Rapportering av farligt avfall till avfallsregistret."
      />
      <CardBody>
      <div className="space-y-4">
        <NaturvardsverketReportingSection
          isActive={company.settings?.isNvActive ?? false}
          onToggle={handleToggle}
        />

        {company.settings?.isNvActive && (
          <>
            <NaturvardsverketApiKeysSection
              nvKey={company.settings.nvKey}
              nvSecret={company.settings.nvSecret}
              onOpenModal={() => setIsKeyModalOpen(true)}
            />

            <NaturvardsverketContactSection
              nvContactFullName={company.settings?.nvContactFullName}
              nvContactEmail={company.settings?.nvContactEmail}
              nvContactPhone={company.settings?.nvContactPhone}
              onOpenModal={() => setIsContactModalOpen(true)}
            />

            <NaturvardsverketAutomaticSection
              isAutomatic={company.settings?.isNvAutomatic ?? false}
              onToggle={(isEnabled) => {
                patchCompanySettingsMutation.mutate({
                  id: company.id,
                  payload: { isNvAutomatic: isEnabled },
                });
              }}
            />

            {company.settings?.isNvAutomatic && (
              <NaturvardsverketNotificationsSection />
            )}
          </>
        )}
      </div>

      <Modal
        isOpen={isKeyModalOpen}
        onClose={() => setIsKeyModalOpen(false)}
        afterClose={() => resetKey()}
        title="Ange API-nyckel"
        actionText="Spara"
        actionHandler={onSubmitKey}
        isLoading={patchCompanySettingsMutation.isPending}
        loadingText="Sparar..."
      >
        <NaturvardsverketKeyModal register={registerKey} errors={errorsKey} />
      </Modal>

      <Modal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        afterClose={() => resetContact()}
        title="Kontaktuppgifter"
        actionText="Spara"
        actionHandler={onSubmitContact}
        isLoading={patchCompanySettingsMutation.isPending}
        loadingText="Sparar..."
      >
        <NaturvardsverketContactModal
          register={registerContact}
          errors={errorsContact}
        />
      </Modal>
      </CardBody>
    </Card>
  );
};

export const hasNvKeyExpired = (nvKeyLastChangedAt?: string) =>
  isBefore(new Date(nvKeyLastChangedAt!), addYears(new Date(), -1));
